import { MatrixClient } from 'matrix-js-sdk/src/matrix';

import logger from 'utils/logging/faro';

export interface BootstrapCrossSigningResult {
  shouldPromptSecurityCode: boolean;
  /**
   * Set when the account has cross-signing keys on the server but this device cannot use them yet.
   * The pre-login unlock gate (see `useStartMatrixClient`) should have run already; this flag is the
   * explicit safety-net signal so the caller never silently treats server keys as local readiness.
   */
  needsSecretStorageUnlock?: boolean;
}

/**
 * Bootstrap cross-signing for an account that does not yet have cross-signing keys uploaded.
 * If the account already has cross-signing keys uploaded, bootstrap is skipped and the caller
 * is expected to drive verification or reset separately.
 *
 * Returns a flag instructing the caller whether the security code create modal should be
 * shown. The modal is **not** shown from inside this hook — showing it here (before
 * `mxLoggedIn` flips to `true`) caused the modal to flash and disappear when the auth
 * router transitioned to the main app. The caller (`useStartMatrixClient`) shows the modal
 * after `setMxLoggedIn(true)` so it appears stably.
 */
const useBootstrapCrossSigning = () => {
  const bootstrapCrossSigning = async (
    matrixClient: MatrixClient,
    userId: string
  ): Promise<BootstrapCrossSigningResult> => {
    const crypto = matrixClient.getCrypto();
    if (!crypto) return { shouldPromptSecurityCode: false };

    const isCrossSigningReady = await crypto.isCrossSigningReady();
    // downloadUncached=true: on a fresh device the local store is empty, so the cached-only check
    // would false-negative for a returning user and wrongly enter the first-time bootstrap below.
    const hasCrossSigningKeys = await crypto.userHasCrossSigningKeys(userId, true);

    if (!isCrossSigningReady && !hasCrossSigningKeys) {
      await crypto.bootstrapCrossSigning({
        setupNewCrossSigning: true,
        authUploadDeviceSigningKeys: async (makeRequest) =>
          makeRequest(null)
            .then(() => logger.info('device signing keys uploaded during cross-signing bootstrap'))
            .catch((error) =>
              logger.error('error uploading device signing keys during cross-signing bootstrap', error)
            ),
      });
      return { shouldPromptSecurityCode: true };
    }

    if (!isCrossSigningReady && hasCrossSigningKeys) {
      logger.info(
        'cross-signing keys exist server-side but device is not ready — secret storage unlock required',
        userId
      );
      return { shouldPromptSecurityCode: false, needsSecretStorageUnlock: true };
    }

    // `isCrossSigningReady` returns true when `privateKeysInSecretStorage === true`, even if
    // the private keys have never been decrypted locally (the "poisoned account" case — see
    // checkNeedsSecretStorageUnlock). The pre-sync probe should have caught this already, but
    // detect it here as a belt-and-suspenders safety net in case the probe missed it (e.g. a
    // reload where partial IDB left cross-signing appearing ready).
    const crossSigningStatus = await crypto.getCrossSigningStatus();
    if (crossSigningStatus.privateKeysInSecretStorage && !crossSigningStatus.privateKeysCachedLocally.masterKey) {
      logger.warn('cross-signing ready via server storage only — secret storage unlock still required', userId);
      return { shouldPromptSecurityCode: false, needsSecretStorageUnlock: true };
    }

    logger.info('cross-signing is already setup, bootstrap skipped', userId);
    return { shouldPromptSecurityCode: false };
  };

  return bootstrapCrossSigning;
};

export default useBootstrapCrossSigning;
